import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, RefreshCw, CheckCircle2, Globe, BarChart3, FileCode, ExternalLink, Copy, Check } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import PortalLayout from '../../../components/PortalLayout';
import axios from 'axios';

const SEOManagement = () => {
  const { token } = useAuth();
  const toast = useToast();
  const [sitemapStatus, setSitemapStatus] = useState(null);
  const [seoStats, setSeoStats] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const SITE_URL = 'https://www.ascarii.com';

  useEffect(() => {
    fetchSEOData();
  }, []);

  const fetchSEOData = async () => {
    setLoading(true);
    try {
      const sitemapRes = await axios.get(`${BACKEND_URL}/api/sitemap/status`);
      setSitemapStatus(sitemapRes.data);

      const [blogRes, landingRes, faqRes, productsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/blog/posts?limit=1`),
        axios.get(`${BACKEND_URL}/api/portal/landing-pages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/faqs`),
        axios.get(`${BACKEND_URL}/api/content/products`)
      ]);

      const productCount = Object.values(productsRes.data).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);

      setSeoStats({
        blogPosts: blogRes.data.total || 0,
        landingPages: landingRes.data.pages?.length || 0,
        faqs: faqRes.data.faqs?.length || 0,
        products: productCount
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const regenerateSitemap = async () => {
    setRegenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/sitemap/regenerate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sitemap regenerated successfully');
      setTimeout(() => fetchSEOData(), 2000);
    } catch (error) {
      toast.error('Error regenerating sitemap');
    } finally {
      setRegenerating(false);
    }
  };

  const copySitemapURL = () => {
    navigator.clipboard.writeText(`${SITE_URL}/sitemap.xml`);
    setCopied(true);
    toast.success('Sitemap URL copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">SEO Management</h1>
            <p className="text-gray-600">Monitor and manage site SEO configuration</p>
          </div>
          <Button onClick={regenerateSitemap} disabled={regenerating} className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className={`w-5 h-5 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Regenerating...' : 'Regenerate Sitemap'}
          </Button>
        </div>

        {/* Overview Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Globe className="w-8 h-8 text-blue-600" />
              <span className="text-xs font-medium text-gray-500">Sitemap</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{sitemapStatus?.url_count || 0}</div>
            <div className="text-sm text-gray-600">Total URLs</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-8 h-8 text-green-600" />
              <span className="text-xs font-medium text-gray-500">Blog</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{seoStats?.blogPosts || 0}</div>
            <div className="text-sm text-gray-600">Blog Posts</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="w-8 h-8 text-purple-600" />
              <span className="text-xs font-medium text-gray-500">Landing</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{seoStats?.landingPages || 0}</div>
            <div className="text-sm text-gray-600">Landing Pages</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <FileCode className="w-8 h-8 text-orange-600" />
              <span className="text-xs font-medium text-gray-500">Products</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{seoStats?.products || 0}</div>
            <div className="text-sm text-gray-600">Product Pages</div>
          </motion.div>
        </div>

        {/* Copyable Sitemap URL */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100 p-6 mb-8">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Sitemap URL for Google Search Console</h3>
          <div className="flex items-center gap-3">
            <code className="flex-1 px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm font-mono text-gray-900">
              {SITE_URL}/sitemap.xml
            </code>
            <Button onClick={copySitemapURL} variant="outline" className="flex items-center gap-2 border-blue-300 hover:bg-blue-50">
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy URL
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-600 mt-2">Use this URL when submitting your sitemap to Google Search Console</p>
        </div>

        {/* Sitemap Status */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Sitemap Status</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">File Information</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className="flex items-center gap-2 text-sm font-medium text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    {sitemapStatus?.exists ? 'Generated' : 'Not Generated'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">URLs</span>
                  <span className="text-sm font-medium text-gray-900">{sitemapStatus?.url_count || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">File Size</span>
                  <span className="text-sm font-medium text-gray-900">{formatFileSize(sitemapStatus?.file_size)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Last Updated</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(sitemapStatus?.last_modified)}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">URL Breakdown</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Static Pages</span>
                  <span className="text-sm font-medium text-gray-900">36</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Landing Pages</span>
                  <span className="text-sm font-medium text-gray-900">{seoStats?.landingPages || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Blog Posts</span>
                  <span className="text-sm font-medium text-gray-900">{seoStats?.blogPosts || 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 flex gap-4">
            <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700">
              <ExternalLink className="w-4 h-4" />
              View Sitemap
            </a>
            <a href="/schema-test" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700">
              <FileCode className="w-4 h-4" />
              Test Schemas
            </a>
          </div>
        </div>

        {/* SEO Features */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">SEO Implementation</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Unique Page Titles (51 pages)',
              'Meta Descriptions (51 pages)',
              'Canonical URLs (45 pages)',
              'Breadcrumb Schema (All pages)',
              'Product Schema (19 products)',
              'FAQ Schema (' + (seoStats?.faqs || 0) + ' FAQs)',
              'Article Schema (All blog posts)',
              'Image Optimization (93.6% reduction)'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100 p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">SEO Resources</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all">
              <Globe className="w-5 h-5 text-blue-600" />
              <div>
                <div className="text-sm font-semibold text-gray-900">Google Search Console</div>
                <div className="text-xs text-gray-500">Monitor indexing</div>
              </div>
            </a>
            <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all">
              <FileCode className="w-5 h-5 text-purple-600" />
              <div>
                <div className="text-sm font-semibold text-gray-900">Rich Results Test</div>
                <div className="text-xs text-gray-500">Validate schemas</div>
              </div>
            </a>
            <a href="https://pagespeed.web.dev" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all">
              <BarChart3 className="w-5 h-5 text-orange-600" />
              <div>
                <div className="text-sm font-semibold text-gray-900">PageSpeed Insights</div>
                <div className="text-xs text-gray-500">Test performance</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default SEOManagement;
